import { useMemo } from 'react';
import { countMatches } from '../utils/searchHighlight';
import { SectionSearchDocument } from './useSectionsSearchDocument';

/**
 * Deliberately not fuse.js (`useSectionSearch`): fuse re-indexes its whole
 * corpus per construction, tokenizing every field, and this runs on every
 * edit. Equivalent because `threshold: 0` with `ignoreLocation: true` reduces
 * fuse to a case-insensitive substring match.
 */
const countDocumentMatches = (
  document: SectionSearchDocument,
  query: string
): number =>
  countMatches(document.avatarName, query) +
  countMatches(document.sectionName, query) +
  countMatches(document.plainText, query);

export interface SearchStaleness {
  liveMatchCount: number | undefined;
  isStale: boolean;
}

interface UseSearchStalenessParams {
  /** the sections as they read right now */
  liveDocument: SectionSearchDocument[];
  matchedSectionIds: Set<string> | null;
  /** the frozen match total the highlights on screen add up to */
  matchCount: number | null;
  query: string;
}

/**
 * @description the number of individual matches for `query` across the project
 * as of right now, which drifts from the frozen highlights as the user edits.
 *
 * Reported in the same unit as `matchCount` so the bar can show one number:
 * the live one, with `isStale` marking that the results below no longer add up
 * to it.
 */
const useSearchStaleness = ({
  liveDocument,
  matchedSectionIds,
  matchCount,
  query,
}: UseSearchStalenessParams): SearchStaleness =>
  useMemo(() => {
    // with no search running there is nothing to count, so there is also
    // nothing that can be seen to be out of date
    if (!matchedSectionIds) {
      return { liveMatchCount: undefined, isStale: false };
    }

    const { liveMatchCount, hasMembershipDrift } = liveDocument.reduce(
      (totals, live) => {
        const matches = countDocumentMatches(live, query);
        return {
          liveMatchCount: totals.liveMatchCount + matches,
          hasMembershipDrift:
            totals.hasMembershipDrift ||
            matches > 0 !== matchedSectionIds.has(live.id),
        };
      },
      { liveMatchCount: 0, hasMembershipDrift: false }
    );

    // Both conditions are needed. The count alone misses a swap - one section
    // loses a match as another gains one - which leaves the total unchanged
    // while the visible set is wrong. Membership alone misses a section that
    // gains or loses an occurrence without crossing zero, and sections that
    // were added or deleted never appear in `matchedSectionIds`.
    const isStale = hasMembershipDrift || liveMatchCount !== matchCount;

    return { liveMatchCount, isStale };
  }, [liveDocument, matchedSectionIds, matchCount, query]);

export default useSearchStaleness;
